<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  PAvatar,
  PAvatarSize,
  PAvatarType,
  PBtnBadge,
  PCard,
  PCardSize,
  PFloatingPanelDialog,
  PGlobalMenu,
  PGlobalMenuItem,
  PGlobalMenuWithSubMenu,
  PMaxLines,
  PMenu,
  PSpace,
  PSpaceSize,
  PTextTitle2,
  usePrevioDS,
} from '@previo/design-system'
import { Color } from '@previo/codebooks'
import { $t } from '@previo/core'
import { Routes } from '~/router/routes'
import { CrmModule, usePermissionChecker } from '~/composables/usePermissionChecker'
import router from '~/router'
import { useUserStore } from '~/store/user'
import PropertySearch from '~/layouts/components/app-menu/property-search/PropertySearch.vue'
import UserOptionsList from '~/layouts/components/app-menu/UserOptionsList.vue'

const ds = usePrevioDS()
const openMenu = ref<boolean>(false)
const userOptionsMenuVisible = ref<boolean>(false)
const { user } = useUserStore()
const { canViewEmployeeModule, isRoot } = usePermissionChecker()

const routeModuleMap: Partial<Record<Routes, CrmModule>> = {
  [Routes.SALES_DASHBOARD_INDEX]: CrmModule.SALES_DASHBOARD,
  [Routes.SALES_COMMISSIONS_INDEX]: CrmModule.SALES_COMMISSIONS,
  [Routes.SALES_CONTRACTS_INDEX]: CrmModule.SALES_DOCUMENTS,
  [Routes.SALES_ELECTRONIC_CONTRACTS_INDEX]: CrmModule.SALES_DOCUMENTS,
  [Routes.SALES_EVENTS_INDEX]: CrmModule.SALES_EVENTS,
  [Routes.SALES_SALES_INDEX]: CrmModule.SALES_SALES,
  [Routes.SALES_REFERENCE_SETS_INDEX]: CrmModule.SALES_SALES,
  [Routes.SALES_SALESMEN_INDEX]: CrmModule.SALESMEN,
  [Routes.PROPERTIES_INDEX]: CrmModule.PROPERTIES,
  [Routes.PROPERTIES_REVIEWS]: CrmModule.PROPERTIES_REVIEWS,
  [Routes.PROPERTIES_FRAUDS]: CrmModule.PROPERTIES_FRAUDS,
  [Routes.PROPERTIES_RESERVATIONS]: CrmModule.PROPERTIES_RESERVATIONS,
  [Routes.PROPERTIES_CONFIRMED_UNSUCCESSFUL_RESERVATIONS]: CrmModule.PROPERTIES_RECALL,
  [Routes.PROPERTIES_MAP]: CrmModule.PROPERTIES_MAP,
  [Routes.BACKOFFICE_CONTACT_PERSON_INDEX]: CrmModule.CONTACT_PERSONS,
  [Routes.OTA_PARTNERS]: CrmModule.OTA_PARTNERS,
  [Routes.OTA_COOPERATION]: CrmModule.OTA_COOPERATION,
  [Routes.OTA_CM_ROOMS_PLANNING]: CrmModule.OTA_CM,
  [Routes.OTA_CM_REQUEST]: CrmModule.OTA_CM,
  [Routes.OTA_CM_LOGIN_CREDENTIALS]: CrmModule.OTA_CM,
  [Routes.OTA_CM_RESERVATIONS]: CrmModule.OTA_CM,
  [Routes.OTA_CM_STATUS]: CrmModule.OTA_CM,
  [Routes.OTA_CM_DASHBOARD]: CrmModule.OTA_CM,
  [Routes.OTA_DEV_TOOLS_CHM_CONNECTIONS]: CrmModule.OTA_CM,
  [Routes.OTA_DEV_TOOLS_RESERVATION_ROOM_LOOKUP]: CrmModule.OTA_CM,
  [Routes.OTA_DEV_TOOLS_ICAL_VIEWER]: CrmModule.OTA_CM,
  [Routes.INVOICE_FEE_INVOICES]: CrmModule.INVOICES,
  [Routes.INVOICE_SALES_INVOICES]: CrmModule.INVOICES,
  [Routes.INVOICE_COMMISSION_INVOICES]: CrmModule.INVOICES,
  [Routes.INVOICE_PERFORMANCE_INVOICES]: CrmModule.INVOICES,
  [Routes.INVOICE_FEES_REPORT]: CrmModule.INVOICES,
  [Routes.INVOICE_COMMISSION_REPORT]: CrmModule.INVOICES,
  [Routes.INVOICE_COMMISSION_PER_HOTEL]: CrmModule.INVOICES,
  [Routes.INVOICE_IMPORT_BANK_STATEMENTS]: CrmModule.INVOICES,
  [Routes.INVOICE_BANK_OPERATIONS]: CrmModule.INVOICES,
  [Routes.USERS_INDEX]: CrmModule.USERS,
  [Routes.USERS_EMPLOYEES]: CrmModule.EMPLOYEES,
  [Routes.USERS_API_USERS]: CrmModule.API_USERS,
  [Routes.USERS_API_USERS_INTEGRATION_PARTNERS]: CrmModule.INTEGRATION_PARTNERS,
  [Routes.USERS_ROLE]: CrmModule.USERS_ROLES,
  [Routes.USERS_GROUPS]: CrmModule.USERS_GROUPS,
  [Routes.USERS_ACTIVITY]: CrmModule.USERS_ACTIVITY,
  [Routes.SYSTEM_PAY_TERMINALS]: CrmModule.SYSTEM_PREVIO_PAY,
  [Routes.SYSTEM_PAY_ONLINE_GATEWAY]: CrmModule.SYSTEM_PREVIO_PAY,
  [Routes.SYSTEM_SITES]: CrmModule.SYSTEM_SITES,
  [Routes.SYSTEM_ENUMERATIONS_SPA_PROCEDURES]: CrmModule.SYSTEM_SPA_PROCEDURES,
  [Routes.SYSTEM_TIPBOX]: CrmModule.SYSTEM_TIPBOX,
  [Routes.SYSTEM_FEEDBACK]: CrmModule.SYSTEM_FEEDBACK,
  [Routes.SYSTEM_PMS_VERSION_OVERRIDES]: CrmModule.SYSTEM_PMS_VERSION_OVERRIDES,
}

const allMenuItems: PGlobalMenuWithSubMenu[] = [
  new PGlobalMenuWithSubMenu('sale', $t('Sales'), null, [
    new PGlobalMenuItem(
      'sales-dashboard',
      $t('Sales dashboard'),
      null,
      Routes.SALES_DASHBOARD_INDEX,
    ),
    new PGlobalMenuItem(
      'salesman-rewards',
      $t('Salesman rewards'),
      null,
      Routes.SALES_COMMISSIONS_INDEX,
    ),
    new PGlobalMenuItem('contracts', $t('Contracts'), null, Routes.SALES_CONTRACTS_INDEX),
    new PGlobalMenuItem(
      'electronic-contracts',
      $t('E-contracts'),
      null,
      Routes.SALES_ELECTRONIC_CONTRACTS_INDEX,
    ),
    new PGlobalMenuItem('events', $t('Events'), null, Routes.SALES_EVENTS_INDEX),
    new PGlobalMenuItem('sales', $t('Sold services'), null, Routes.SALES_SALES_INDEX),
    new PGlobalMenuItem('sales-cases', $t('Sales cases'), null, Routes.SALES_CASES_INDEX),
    new PGlobalMenuItem(
      'reference-sets',
      $t('Reference sets'),
      null,
      Routes.SALES_REFERENCE_SETS_INDEX,
    ),
    new PGlobalMenuItem('salesmen', $t('Salesmen'), null, Routes.SALES_SALESMEN_INDEX),
  ]),
  new PGlobalMenuWithSubMenu('hotels', $t('Hotels'), null, [
    new PGlobalMenuItem('list-of-hotels', $t('List of hotels'), null, Routes.PROPERTIES_INDEX),
    new PGlobalMenuItem(
      'contact-persons',
      $t('Contact persons'),
      null,
      Routes.BACKOFFICE_CONTACT_PERSON_INDEX,
    ),
    new PGlobalMenuItem('review', $t('Review'), null, Routes.PROPERTIES_REVIEWS),
    new PGlobalMenuItem('frauds', $t('Frauds'), null, Routes.PROPERTIES_FRAUDS),
    new PGlobalMenuItem(
      'confirm-disapproved-reservations',
      $t('Confirmed disapproved reservations'),
      null,
      Routes.PROPERTIES_CONFIRMED_UNSUCCESSFUL_RESERVATIONS,
    ),
    new PGlobalMenuItem('reservation', $t('Reservation'), null, Routes.PROPERTIES_RESERVATIONS),
    new PGlobalMenuItem('hotels-map', $t('Hotels map'), null, Routes.PROPERTIES_MAP),
  ]),
  new PGlobalMenuWithSubMenu('ota', $t('OTA'), null, [
    new PGlobalMenuItem('list-of-partners', $t('List of partners'), null, Routes.OTA_PARTNERS),
    new PGlobalMenuItem('collaboration-pp', $t('Collaboration PP'), null, Routes.OTA_COOPERATION),
    new PGlobalMenuItem('room-mapping', $t('Room mapping'), null, Routes.OTA_CM_ROOMS_PLANNING),
    new PGlobalMenuItem('requests', $t('Requests'), null, Routes.OTA_CM_REQUEST),
    new PGlobalMenuItem(
      'login-credentials',
      $t('Login credentials'),
      null,
      Routes.OTA_CM_LOGIN_CREDENTIALS,
    ),
    new PGlobalMenuItem('reservation', $t('Reservation'), null, Routes.OTA_CM_RESERVATIONS),
    new PGlobalMenuItem('status', $t('Status'), null, Routes.OTA_CM_STATUS),
    new PGlobalMenuItem('dashboard', $t('Dashboard'), null, Routes.OTA_CM_DASHBOARD),
    new PGlobalMenuWithSubMenu('dev-tools', $t('Dev Tools'), null, [
      new PGlobalMenuItem(
        'chm-connections',
        $t('CHM Connections'),
        null,
        Routes.OTA_DEV_TOOLS_CHM_CONNECTIONS,
      ),
      new PGlobalMenuItem(
        'reservation-room-lookup',
        $t('Reservation room lookup'),
        null,
        Routes.OTA_DEV_TOOLS_RESERVATION_ROOM_LOOKUP,
      ),
      new PGlobalMenuItem('ical-viewer', $t('iCal viewer'), null, Routes.OTA_DEV_TOOLS_ICAL_VIEWER),
    ]),
  ]),
  new PGlobalMenuWithSubMenu('invoices', $t('Invoices'), null, [
    new PGlobalMenuItem('fee-invoices', $t('Fee invoices'), null, Routes.INVOICE_FEE_INVOICES),
    new PGlobalMenuItem(
      'sales-invoices',
      $t('License invoices'),
      null,
      Routes.INVOICE_SALES_INVOICES,
    ),
    new PGlobalMenuItem(
      'commission-invoice',
      $t('Commission invoice'),
      null,
      Routes.INVOICE_COMMISSION_INVOICES,
    ),
    new PGlobalMenuItem(
      'performance-invoice',
      $t('Performance invoice'),
      null,
      Routes.INVOICE_PERFORMANCE_INVOICES,
    ),
    new PGlobalMenuItem(
      'fees-report',
      $t('Operating fees report'),
      null,
      Routes.INVOICE_FEES_REPORT,
    ),
    new PGlobalMenuItem('commission', $t('Commissions'), null, Routes.INVOICE_COMMISSION_REPORT),
    new PGlobalMenuItem(
      'commission-per-hotel',
      $t('Commission per hotel'),
      null,
      Routes.INVOICE_COMMISSION_PER_HOTEL,
    ),
    new PGlobalMenuItem(
      'import-bank-statement',
      $t('Import bank statement'),
      null,
      Routes.INVOICE_IMPORT_BANK_STATEMENTS,
    ),
    new PGlobalMenuItem(
      'bank-operations',
      $t('Bank operations'),
      null,
      Routes.INVOICE_BANK_OPERATIONS,
    ),
  ]),
  new PGlobalMenuWithSubMenu('user', $t('Users'), null, [
    new PGlobalMenuItem('users', $t('Users'), null, Routes.USERS_INDEX),
    new PGlobalMenuItem('employees', $t('Employees'), null, Routes.USERS_EMPLOYEES),
    new PGlobalMenuItem('api-users', $t('API users'), null, Routes.USERS_API_USERS),
    new PGlobalMenuItem(
      'integration-partners',
      $t('Integration partners'),
      null,
      Routes.USERS_API_USERS_INTEGRATION_PARTNERS,
    ),
    new PGlobalMenuItem('roles', $t('Roles'), null, Routes.USERS_ROLE),
    new PGlobalMenuItem('groups', $t('Groups'), null, Routes.USERS_GROUPS),
    new PGlobalMenuItem('activity', $t('Users activity'), null, Routes.USERS_ACTIVITY),
  ]),
  new PGlobalMenuWithSubMenu('system', $t('System'), null, [
    new PGlobalMenuWithSubMenu('previo-pay', $t('Previo Pay'), null, [
      new PGlobalMenuItem('terminals', $t('Terminals'), null, Routes.SYSTEM_PAY_TERMINALS),
      new PGlobalMenuItem(
        'online-payment-gateway',
        $t('Online payment gateway'),
        null,
        Routes.SYSTEM_PAY_ONLINE_GATEWAY,
      ),
    ]),
    new PGlobalMenuItem('web-pages', $t('Web pages'), null, Routes.SYSTEM_SITES),
    new PGlobalMenuWithSubMenu('codebooks', $t('Codebooks'), null, [
      new PGlobalMenuItem(
        'spa-treatments',
        $t('Spa treatments'),
        null,
        Routes.SYSTEM_ENUMERATIONS_SPA_PROCEDURES,
      ),
    ]),
    new PGlobalMenuItem('tipbox', $t('Tipbox'), null, Routes.SYSTEM_TIPBOX),
    new PGlobalMenuItem('feedbacks', $t('Feedbacks'), null, Routes.SYSTEM_FEEDBACK),
    new PGlobalMenuItem(
      'pms-version',
      $t('PMS version'),
      null,
      Routes.SYSTEM_PMS_VERSION_OVERRIDES,
    ),
    new PGlobalMenuItem(
      'ntak-version',
      $t('NTAK version'),
      null,
      Routes.SYSTEM_NTAK_VERSION,
      isRoot(),
    ),
  ]),
]

type MenuNode = PGlobalMenuItem | PGlobalMenuWithSubMenu

const filterMenuNode = (node: MenuNode): MenuNode | null => {
  if (node instanceof PGlobalMenuWithSubMenu) {
    const keptChildren = node.items
      .map((child) => filterMenuNode(child))
      .filter((child): child is MenuNode => child !== null)

    if (keptChildren.length === 0) {
      return null
    }

    return new PGlobalMenuWithSubMenu(node.key, node.title, node.icon, keptChildren)
  }

  const module = routeModuleMap[node.route as Routes]
  if (module !== undefined && !canViewEmployeeModule(module)) {
    return null
  }

  return node
}

const menuItems = computed<PGlobalMenuWithSubMenu[]>(() => {
  return allMenuItems
    .map((item) => filterMenuNode(item))
    .filter((item): item is PGlobalMenuWithSubMenu => item instanceof PGlobalMenuWithSubMenu)
})

const navigateTo = (route: string): void => {
  router.push({ name: route })
}

const closeMenu = (): void => {
  openMenu.value = false
}
</script>

<template>
  <p-card
    class="crm-app-menu p-flex p-no-wrap p-items-center"
    :color="Color.PRIMARY"
    :pa-mobile="PCardSize.SM"
  >
    <p-space :desktop="PSpaceSize.MD" :laptop="PSpaceSize.SM" :mobile="PSpaceSize.ZERO" />

    <div class="p-flex p-flex-center">
      <img
        alt="Previo logo"
        height="32"
        src="@previo/design-system/src/public/logo/previo-white.svg"
        width="62"
      />
    </div>

    <p-space :desktop="PSpaceSize.XXL" :laptop="PSpaceSize.MD" :mobile="PSpaceSize.GROW" />

    <p-global-menu
      v-model="openMenu"
      :items="menuItems"
      @close-mobile="closeMenu"
      @navigate-to="navigateTo"
    />

    <p-space :laptop="PSpaceSize.GROW" :mobile="PSpaceSize.SM" />

    <template v-if="canViewEmployeeModule(CrmModule.PROPERTIES)">
      <property-search />
      <p-space :laptop="PSpaceSize.MD" :mobile="PSpaceSize.SM" />
    </template>

    <p-avatar
      class="p-cursor-pointer p-card-avoid-border"
      :size="PAvatarSize.SMALL"
      :text="user.userName"
      :type="PAvatarType.MONOGRAM"
      @click="userOptionsMenuVisible = !userOptionsMenuVisible"
    >
      <p-menu
        v-if="ds.screen().isLaptopAndLarge"
        v-model="userOptionsMenuVisible"
        anchor="bottom end"
        cy="user-options"
        self="top end"
        no-parent-event
      >
        <p-card class="crm-app-menu-user-context-menu" :pa-mobile="PCardSize.MD">
          <p-text-title2 :color="Color.PRIMARY" :max-lines="PMaxLines.ONE" :text="user.userName" />
          <p-space :mobile="PSpaceSize.HEADLINE" vertical />

          <user-options-list />
        </p-card>
      </p-menu>
    </p-avatar>

    <template v-if="ds.screen().isMobile">
      <p-space :mobile="PSpaceSize.SM" />
      <p-btn-badge
        :color="Color.WHITE"
        cy="open-mobile-menu"
        icon-start="bars"
        @click="openMenu = !openMenu"
      />
    </template>

    <p-space :laptop="PSpaceSize.XS" :mobile="PSpaceSize.ZERO" />
  </p-card>

  <p-floating-panel-dialog
    v-if="ds.screen().isMobile"
    v-model="userOptionsMenuVisible"
    :card-size="PCardSize.MD"
    cy="user-setting"
    :title="user.userName"
  >
    <user-options-list />
  </p-floating-panel-dialog>
</template>

<style lang="scss" scoped>
@import '@previo/design-system/src/assets/scss/shared/mixins';

.crm-app-menu-user-context-menu {
  width: 248px;
}

.crm-app-menu {
  height: 56px;

  @include laptopAndLarge {
    height: 52px;
  }
}
</style>
